import { Component, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { PreviewDetailsDialogComponent } from '../../dialogs/preview-details-dialog/preview-details-dialog.component';

type EmploymentStatus = 'ACTIVE' | 'PROBATION';

@Component({
  selector: 'app-leave-balance-detail',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, FormsModule, MatIconModule],
  templateUrl: './leave-balance-detail.component.html',
  styleUrls: ['./leave-balance-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LeaveBalanceDetailComponent implements ICellRendererAngularComp {
  public detailData: any = {};
  public remarksDraft = '';
  public isSavingRemarks = false;
  private params?: ICellRendererParams;

  constructor(private dialog: MatDialog) {}

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.detailData = params.data || {};
    this.remarksDraft = '';
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.detailData = params.data || {};
    this.remarksDraft = '';
    return true;
  }

  get employmentStatus(): EmploymentStatus {
    return (this.detailData?.employmentStatus as EmploymentStatus) || 'ACTIVE';
  }

  get isProbation(): boolean {
    return this.employmentStatus === 'PROBATION';
  }

  get leaveTypeBalances(): any[] {
    const v = this.detailData?.leaveTypeBalances;
    return Array.isArray(v) ? v : [];
  }

  /** Total carried-forward leaves summed across all leave types */
  get totalCarryForward(): number {
    return this.leaveTypeBalances.reduce((sum, b) => sum + (b.carriedForwardLeaves ?? 0), 0);
  }

  /** True when the SP has returned the RESERVED_LEAVES / AVAILABLE_BALANCE columns */
  get hasReservedLeaves(): boolean {
    return this.leaveTypeBalances.some(b => b.reservedLeaves != null);
  }

  get optionalHolidayTotal(): number {
    const raw =
      this.detailData?.optionalHolidayTotal ??
      this.detailData?.optionalHolidayCount ??
      this.detailData?.optionalHolidaysCount ??
      this.detailData?.optionalHolidays?.length ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  get compOffTotal(): number {
    const raw =
      this.detailData?.compOffTotal ??
      this.detailData?.compOff ??
      this.detailData?.compOffBalance ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  get remarksList(): string[] {
    const text = this.detailData?.remarks?.text ?? '';
    console.log('[LeaveBalanceDetail] remarks.text =', text);
    if (!text) return [];
    // SP separates remarks with "   ," (3 spaces + comma) — use that as delimiter
    return text.split(/\s{2,},\s*/).map((s: string) => s.trim()).filter((s: string) => s.length > 0);
  }

  onPreviewOptionalHolidays(): void {
    const items = Array.isArray(this.detailData?.optionalHolidays)
      ? this.detailData.optionalHolidays
      : [];

    this.dialog.open(PreviewDetailsDialogComponent, {
      width: '600px',
      panelClass: 'lm-dialog-panel',
      data: {
        type: 'OPTIONAL_HOLIDAY',
        employeeName: this.detailData.employeeName ?? '',
        items,
        note: this.detailData.optionalHolidayNote ?? '',
      },
    });
  }

  onPreviewCompOffDays(): void {
    const items = Array.isArray(this.detailData?.compOffDays)
      ? this.detailData.compOffDays
      : [];

    this.dialog.open(PreviewDetailsDialogComponent, {
      width: '750px',
      panelClass: 'lm-dialog-panel',
      data: {
        type: 'COMP_OFF',
        employeeName: this.detailData.employeeName ?? '',
        balance: this.compOffTotal,
        items,
      },
    });
  }

  async saveRemarks(): Promise<void> {
    const employeeId = String(this.detailData?.employeeId ?? '').trim();
    if (!employeeId) return;

    const newRemark = String(this.remarksDraft ?? '').trim();
    if (!newRemark) return;

    // Update local UI immediately (append for display)
    const existingText = String(this.detailData?.remarks?.text ?? '').trim();
    const updatedText = existingText ? `${existingText}   ,${newRemark}` : newRemark;

    this.isSavingRemarks = true;
    this.detailData = {
      ...this.detailData,
      remarks: { text: updatedText, hasInfo: true },
    };

    // Send ONLY the new remark — SP appends internally
    const callback = (this.params as any)?.onSaveRemarks;
    try {
      if (typeof callback === 'function') {
        await Promise.resolve(callback(employeeId, newRemark));
      }
    } finally {
      this.isSavingRemarks = false;
      this.remarksDraft = '';
    }
  }

  openRemarksDialog(): void {
    const remarks = this.remarksList;
    const employeeName = this.detailData?.employeeName || 'Employee';
    
    this.dialog.open(RemarksDialogComponent, {
      width: '600px',
      panelClass: 'lm-dialog-panel',
      data: {
        employeeName,
        remarks,
      },
    });
  }
}

// Simple inline dialog component for displaying remarks
import { Component as NgComponent, Inject } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoPipe } from '@jsverse/transloco';

@NgComponent({
  selector: 'app-remarks-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="remarks-dialog">
      <div class="remarks-dialog-header">
        <h2>Remarks - {{ data.employeeName }}</h2>
        <button class="close-btn" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="remarks-dialog-body">
        <div *ngIf="data.remarks.length === 0" class="no-remarks">
          No remarks available
        </div>
        <div *ngIf="data.remarks.length > 0" class="remarks-list">
          <div class="remark-item" *ngFor="let remark of data.remarks; let i = index">
            <span class="remark-index">{{ i + 1 }}.</span>
            <span class="remark-text">{{ remark }}</span>
          </div>
        </div>
      </div>
      <div class="remarks-dialog-footer">
        <button mat-button (click)="close()">Close</button>
      </div>
    </div>
  `,
  styles: [`
    .remarks-dialog {
      display: flex;
      flex-direction: column;
      max-height: 80vh;
    }
    .remarks-dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid #e5e7eb;
    }
    .remarks-dialog-header h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
    }
    .close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      color: #6b7280;
    }
    .close-btn:hover {
      background: #f3f4f6;
    }
    .remarks-dialog-body {
      flex: 1;
      padding: 20px 24px;
      overflow-y: auto;
      min-height: 200px;
      max-height: 60vh;
    }
    .no-remarks {
      text-align: center;
      color: #9ca3af;
      padding: 40px 20px;
      font-size: 14px;
    }
    .remarks-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .remark-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 12px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
    }
    .remark-index {
      color: #6b7280;
      font-size: 13px;
      font-weight: 600;
      min-width: 24px;
    }
    .remark-text {
      flex: 1;
      font-size: 14px;
      color: #374151;
      line-height: 1.5;
    }
    .remarks-dialog-footer {
      padding: 16px 24px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: flex-end;
    }
  `],
})
export class RemarksDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<RemarksDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeName: string; remarks: string[] }
  ) {}

  close(): void {
    this.dialogRef.close();
  }
}
